/**
 * driveProjectStore — Store principal que orquestra projetos, referências, dados de pesquisa, cruzamentos, grupos e campos
 *
 * Consumidores: 56+ componentes (App, Dashboard, Inspector, Conexoes, DadosPesquisa, Leituras, etc.)
 * Dependências: googleDrive, outlineStore, slices (project, reference, researchData, crossingOutline, group, customField)
 * Persistência: Google Drive (pasta do projeto no Drive do usuário)
 */
import { create } from 'zustand';
import type { DriveProjectStore } from './driveProject/types';
import { createProjectSlice } from './driveProject/projectSlice';
import { createReferenceSlice } from './driveProject/referenceSlice';
import { createResearchDataSlice } from './driveProject/researchDataSlice';
import { createCrossingOutlineSlice } from './driveProject/crossingOutlineSlice';
import { createGroupSlice } from './driveProject/groupSlice';
import { createCustomFieldSlice } from './driveProject/customFieldSlice';

// Re-export types e constantes para manter backward compatibility
// (45 arquivos importam daqui — nenhum import precisa mudar)
export type {
  ProjectOnboardingAnswers,
  DataGroup,
  FieldType,
  ProjectField,
  FieldValue,
  LensRole,
  DataLens,
  TimeMarker,
  ProjectResearchData,
  ProjectReference,
  DriveProject,
} from './driveProject/types';
export { LENS_ROLES } from './driveProject/types';

// ============================================
// STORE PRINCIPAL — Orquestrador
// ============================================

export const useDriveProjectStore = create<DriveProjectStore>((set, get) => ({
  // Estado inicial
  projects: [],
  isLoading: false,
  error: null,
  activeProjectId: null, // SEGURANÇA: não carregar de localStorage na inicialização
  projectsLoaded: false,
  projectsLoadedAt: null,

  // Cache de referências
  cachedReferences: [],
  referencesLoaded: false,
  referencesLoadedAt: null,
  isLoadingReferences: false,

  // Cache de dados de pesquisa
  cachedResearchData: [],
  researchDataLoaded: false,
  researchDataLoadedAt: null,
  isLoadingResearchData: false,

  // Cache de cruzamentos
  cachedCrossings: [],
  crossingsLoaded: false,
  crossingsLoadedAt: null,

  // Cache de outline
  cachedOutline: null,
  outlineLoaded: false,
  outlineLoadedAt: null,

  // Cache de grupos (Fase 3)
  cachedGroups: [],
  groupsLoaded: false,
  groupsLoadedAt: null,

  // Cache de campos customizáveis (Fase 7)
  cachedCustomFields: [],
  customFieldsLoaded: false,
  customFieldsLoadedAt: null,

  // Compor slices por domínio
  ...createProjectSlice(set, get),
  ...createReferenceSlice(set, get),
  ...createResearchDataSlice(set, get),
  ...createCrossingOutlineSlice(set, get),
  ...createGroupSlice(set, get),
  ...createCustomFieldSlice(set, get),
}));

// ============================================
// GARANTIA DE ARRAYS SEGUROS - CORREÇÃO CRÍTICA
// ============================================

const ensureArraysAreSafe = () => {
  const state = useDriveProjectStore.getState();
  const updates: Partial<DriveProjectStore> = {};
  let needsUpdate = false;

  if (!Array.isArray(state.cachedReferences)) {
    updates.cachedReferences = [];
    needsUpdate = true;
  }
  if (!Array.isArray(state.cachedResearchData)) {
    updates.cachedResearchData = [];
    needsUpdate = true;
  }
  if (!Array.isArray(state.projects)) {
    updates.projects = [];
    needsUpdate = true;
  }
  if (!Array.isArray(state.cachedGroups)) {
    updates.cachedGroups = [];
    needsUpdate = true;
  }
  if (!Array.isArray(state.cachedCrossings)) {
    updates.cachedCrossings = [];
    needsUpdate = true;
  }
  if (!Array.isArray(state.cachedCustomFields)) {
    updates.cachedCustomFields = [];
    needsUpdate = true;
  }

  if (needsUpdate) {
    useDriveProjectStore.setState(updates);
  }
};

ensureArraysAreSafe();
useDriveProjectStore.subscribe(ensureArraysAreSafe);
